import {
	createUploadDrainEngine,
	FILE_UPLOAD_CONCURRENCY,
	isConfirmedMigrationIntegrityError,
	isUploadTerminalFailure,
	isUploadVerificationPending,
	newUploadId,
	runGuardedEffect,
	withFileUploadConcurrencySlot,
	type DurableLeaseToken,
	type DurableOwnershipPort,
	type UploadDrainEngine
} from "@uploads";
import {
	computeFileUploadDrainDelayMsFor,
	computeNextFileUploadDrainDelayMs,
	drainCancelledUploadSessions,
	ensureUploadOwnerLivenessLockHeld,
	ensureUploadOwnerLivenessLockHeldForOwner,
	getFileUploadLaneIntentLiveness,
	getFileUploadSessionById,
	getPendingFileUploadSessions,
	getTerminallyParkedFileUploadSessions,
	IdbStallError,
	isFileUploadRetryDue,
	isIdbInternalError,
	hasUploadOwnerLockManager,
	isUploadOwnerLockFree,
	requeueNeedsAttentionFileUploadSessions,
	resetUploadingSessions,
	sweepStaleLaneLeases,
	updateFileUploadSessionStatus,
	type FileUploadLaneIntentLiveness,
	type FileUploadSession
} from "@/db/fileUploadDb";
import { stoppedUploadMessage } from "@/helpers/uploadErrorMessages";
import { calculateTimeout } from "@/helpers/utilities";
import { logStorageEstimate, UploadCancelledError } from "@/services/chunkedUploadService";
import { RESUME_OUTCOME } from "@/services/uploads/adminResumeSession";
import { commitExactClaimTerminalWrite, createAdminOwnedOrUnownedGate } from "./adminPersistedSessionReconciler";
import { createAdminUploadWakePolicy } from "./adminUploadWakePolicy";
import { RECOVERY_BLOCKED_ROW_MESSAGE, RECOVERY_BLOCKED_TOAST_MESSAGE, uploadStartRetryMessage } from "./adminUploadRecoveryCopy";
import { getAdminDurableOwnershipForCoordinator } from "./adminUploadLease";
import { adminUploadScopeMismatch, readCurrentAdminUploadScope, type AdminUploadScope } from "./adminUploadAuthScope";
import { drainCancelledAdminUploadSessions } from "./adminCancelledUploadDrain";
import { backgroundUploadAttemptsInFlight, beginBackgroundUploadAttempt, endBackgroundUploadAttempt } from "./uploadActivity";
import { ensureAdminUploadBridgeReady } from "./adminUploadMigrationIntegration";
import { redactUploadLogText, uploadFailureLogDetails } from "./uploadLogRedaction";

/**
 * R-1 ("there is no destroy", owner directive 2026-08-26): the LOST outcome no longer means "the
 * server said this upload is gone" — that verdict now reinitializes the session from its retained
 * buffers and comes back as RETRY. LOST is reached only once the local bytes themselves are
 * missing or incomplete, so the copy says what is actually true instead of implying the app gave up
 * on a file it still holds.
 */
const LOST_MESSAGE = "We no longer have a local copy of this file, so we can't finish it. Please upload it again.";

/**
 * Row copy for the two "we could not finish this pass, and the app will simply come back" cases.
 *
 * Both used to render as a bare, message-less red "Upload failed" — a verdict the app then
 * contradicted by silently retrying. R-0's rule is the opposite: an unfinished pass is a STATUS.
 * The wording deliberately mirrors the dialog's own exhausted-ladder copy ("we'll keep retrying
 * automatically") so the same event reads the same everywhere the user can see it.
 */
const resumeRetryMessage = (fileName: string | undefined) =>
	`We hit a problem resuming ${fileName || "this file"} — we'll keep trying automatically.`;
const unexpectedOutcomeMessage = (fileName: string | undefined) =>
	`We couldn't finish ${fileName || "this file"} on this attempt — we'll keep trying automatically.`;

/** setTimeout saturates past ~24.8 days; every R-0 backoff window is minutes, but clamp regardless. */
const MAX_DUE_DRAIN_DELAY_MS = 2_147_483_000;
const formatCount = (count: number) => `${count} upload${count === 1 ? "" : "s"}`;

interface AdminUploadDrainContext {
	controllers: Map<string, AbortController>;
	counts: {
		resumed: number;
		failed: number;
		lost: number;
		handedOff: number;
		needsAttention: number;
		recoveryBlocked: number;
		pendingRetry: number;
	};
	hasDiscoveredPending: boolean;
	retryingIds: Set<string>;
	verificationPendingIds: Set<string>;
	failedIds: Set<string>;
	recoveryBlockedIds: Set<string>;
	schedulerLost: boolean;
	bridgeReady: boolean;
	/** A pass admitted without the scheduler may maintain rows, but must never start uploads. */
	maintenanceOnly?: boolean;
	/** R-0: earliest absolute instant at which a still-parked session becomes requeue-eligible again. */
	parkedRequeueDeadlineAt?: number;
	/**
	 * The UNFILTERED `status:"pending"` rows `discoverPending` read this pass, kept so `afterDrain`
	 * can compute the R-0 timed wake without re-scanning the same rows. Absent when discovery bailed
	 * before the read (scheduler lease lost, or an IDB error), which is the only case that still pays
	 * for a fresh scan.
	 */
	pendingSessionSnapshot?: FileUploadSession[];
	/**
	 * LSP-19609: lane-intent liveness verdicts probed this pass, by lane id. `claimDrain`'s
	 * actionability preflight fills it (one probe per distinct lane) and `discoverPending` reuses it,
	 * so the same lanes are not probed twice in one pass.
	 */
	laneIntentLivenessByLaneId?: Map<string, FileUploadLaneIntentLiveness>;
}

interface AdminUploadDrainControls extends UploadDrainEngine {
	scheduleRecovery(resetBackoff?: boolean): void;
	clearRecovery(): void;
	runStartupReclaim(): Promise<void>;
}

const ADMIN_SCHEDULER_LEASE_TTL_MS = 2 * 60 * 1000;

// LSP-19561: bound how often the lane-lease GC sweep runs — it only needs to run roughly once per
// several TTL periods, not on every ~30s discoverPending poll tick.
export const LANE_LEASE_GC_THROTTLE_MS = 60 * 60 * 1000; // once per hour
let lastLaneLeaseGcSweepAt = 0;

export function resetLaneLeaseGcThrottleForTests(): void {
	lastLaneLeaseGcSweepAt = 0;
}

async function sweepStaleLaneLeasesIfDue(now: number): Promise<void> {
	if (now - lastLaneLeaseGcSweepAt < LANE_LEASE_GC_THROTTLE_MS) return;
	lastLaneLeaseGcSweepAt = now;
	try {
		await sweepStaleLaneLeases(now);
	} catch (error) {
		lastLaneLeaseGcSweepAt = 0;
		console.warn("[Upload] lane-lease GC sweep failed; will retry on the next drain pass", { error });
	}
}

async function runWithSchedulerHeartbeat(
	context: AdminUploadDrainContext,
	ownership: Awaited<ReturnType<typeof getAdminDurableOwnershipForCoordinator>>,
	token: DurableLeaseToken,
	run: () => Promise<boolean>
): Promise<boolean> {
	const renewScheduler = ownership?.renewScheduler;
	if (!renewScheduler) return run();
	let stopped = false;
	let renewalInFlight: Promise<void> | undefined;
	const markLost = () => {
		if (context.schedulerLost) return;
		context.schedulerLost = true;
		for (const controller of context.controllers.values()) controller.abort(new Error("Upload scheduler lease was lost"));
	};
	const heartbeat = async () => {
		if (stopped || context.schedulerLost || renewalInFlight) return;
		renewalInFlight = renewScheduler({ token, ttlMs: ADMIN_SCHEDULER_LEASE_TTL_MS })
			.then((renewed) => {
				if (!renewed) markLost();
			})
			.catch(() => markLost())
			.finally(() => {
				renewalInFlight = undefined;
			});
		await renewalInFlight;
	};
	const heartbeatTimer = setInterval(() => void heartbeat(), Math.max(1_000, Math.floor(ADMIN_SCHEDULER_LEASE_TTL_MS / 3)));
	try {
		return await run();
	} finally {
		stopped = true;
		clearInterval(heartbeatTimer);
		if (renewalInFlight) await renewalInFlight;
	}
}

/**
 * LSP-19384: hand the tray the rows automatic retry has given up on.
 *
 * Presentation only — no controller is attached and no attempt is started, which is the whole
 * difference between this and a resumed row. It owns its own error handling so the caller can start
 * it without awaiting it and without risking an unhandled rejection.
 */
async function surfaceTerminallyParkedUploads(host: any, isStillCurrent: () => boolean): Promise<void> {
	try {
		// Read time, captured BEFORE the read. The tray uses it to ignore a snapshot that predates a
		// local Try again or Remove — without it, an in-flight read from before the click lands after
		// it and re-renders the row the user just resolved (break-it round 1).
		const readAt = Date.now();
		const parkedSessions = await getTerminallyParkedFileUploadSessions();
		// The drain's own two `disposed` checks exist so a discovery started before teardown cannot
		// mutate the dialog afterwards. This call is started before the first of them and is not
		// awaited, so it has to re-ask for itself — otherwise a read that resolves after the user
		// navigates away pops the tray open on the page they just moved to.
		if (!isStillCurrent()) return;
		const dialog = host.$refs.fileUploadDialog;
		if (typeof dialog?.showTerminallyParkedUploads !== "function") {
			// Silent-failure rule. Both halves of the old optional-call form swallowed: an unmounted
			// dialog ref (routine on the first pass of a page load) and a host facade without the
			// method produced ZERO output, which is indistinguishable from "there were no parked
			// rows" — and the sessions had already been read out of IndexedDB and dropped.
			if (parkedSessions.length > 0) {
				console.warn("[Upload] Parked upload sessions could not be surfaced: the upload tray is unavailable", {
					sessionCount: parkedSessions.length,
					sessionIds: parkedSessions.map((session) => session.id)
				});
			}
			return;
		}
		dialog.showTerminallyParkedUploads(
			parkedSessions.map((session) => ({
				sessionId: session.id,
				uploadId: session.uploadId,
				fileName: session.fileName,
				fileSize: session.fileSize,
				fileType: session.fileType,
				status: session.status,
				lastError: session.lastError
			})),
			readAt
		);
	} catch (error) {
		console.warn("[Upload] Could not surface parked upload sessions in the tray", uploadFailureLogDetails(error));
	}
}

export interface AdminUploadDrainOptions {
	/** LSP-22950 O8: the current auth scope; defaults to the auth session store. Injectable for tests. */
	readUploadScope?: () => AdminUploadScope | undefined;
}

export function createAdminUploadDrain(
	host: any,
	rearmSmallFileUploads?: () => void,
	drainOptions: AdminUploadDrainOptions = {}
): AdminUploadDrainControls {
	const readUploadScope = drainOptions.readUploadScope ?? readCurrentAdminUploadScope;
	// Latched on WHICH rows are held under WHICH scope, so a recovery wake every few seconds logs a change, not a pass.
	let lastHeldScopeSignature: string | undefined;
	/**
	 * LSP-22950 O8: hold (never fail or delete) a pending row stamped for a different organization or user than the
	 * current session. Its lazy mint would otherwise run under the wrong identity. The row stays `pending` with its bytes
	 * and is picked up by the first pass after the user switches back. An unreadable scope holds nothing (no evidence).
	 */
	const holdOutOfScopeSessions = (pending: readonly FileUploadSession[]): FileUploadSession[] => {
		const scope = readUploadScope();
		if (!scope) return [...pending];
		const held: Array<{ sessionId: string; mismatch: string; batchOrganizationId: string | null; batchUserId: string | null }> = [];
		const inScope = pending.filter((session) => {
			const mismatch = adminUploadScopeMismatch(session, scope);
			if (!mismatch) return true;
			held.push({
				sessionId: session.id,
				mismatch,
				batchOrganizationId: session.batchOrganizationId ?? null,
				batchUserId: session.batchUserId ?? null
			});
			return false;
		});
		const signature = held.length ? `${scope.organizationId}:${scope.userId}:${held.map((row) => row.sessionId).join(",")}` : undefined;
		if (signature !== lastHeldScopeSignature) {
			lastHeldScopeSignature = signature;
			if (held.length)
				console.warn(
					"[Upload] Holding pending upload rows that belong to a different organization or user than the current session",
					{
						currentOrganizationId: scope.organizationId,
						currentUserId: scope.userId,
						heldCount: held.length,
						heldSessionIds: held.slice(0, 50).map((row) => row.sessionId),
						held: held.slice(0, 50)
					}
				);
		}
		return inScope;
	};
	let skipRecurringReset = false;
	let bridgeReady = false;
	// Keep comparison state private to this drain instance while mirroring snapshots onto `host` for
	// observability and compatibility. Separate closure state prevents overlapping or replacement
	// instances from erasing each other's dedup memory or suppressing a first announcement.
	let lastAnnouncedRetrySessionIds: Set<string> | null = null;
	let lastAnnouncedFailedSessionIds: Set<string> | null = null;
	let lastAnnouncedRecoveryBlockedSessionIds: Set<string> | null = null;
	// LSP-19766: sessions the last recurring pass reported as orphaned-but-not-yet-reclaimable, so the
	// NEXT pass can tell the dialog to clear any that are no longer reported (reclaimed, or genuinely gone).
	let verifyingOrphanSessionIds = new Set<string>();
	// Engine disposal cannot cancel in-flight discovery. Track disposal locally and skip stale
	// completions before they write host mirrors or other state shared with a replacement drain.
	let disposed = false;
	/** Filled in once the engine exists; the R-0 timed wake below drains through it. */
	const engineRef: { current?: UploadDrainEngine } = {};
	let cancelledDrainInFlight: Promise<void> | undefined;
	/**
	 * LSP-19088 — a page load is the user saying "try these now".
	 *
	 * R-0's escalating `retryAt` is real pacing and it is what stopped the LSP-18742 re-pick spin, so
	 * automatic passes keep honoring it exactly as before. But nothing ever forgave it for an ownerless
	 * pending row: `resetUploadingSessions` skips those outright, so a row that escalated onto the slow
	 * tier stayed invisible to the drain for up to fifteen minutes ACROSS reloads. A user watching
	 * "we'll keep retrying automatically" and refreshing to hurry it along changed nothing, and the only
	 * way through was editing IndexedDB by hand (observed while validating LSP-19325).
	 *
	 * The FIRST discovery of each page load therefore ignores `retryAt`, giving every parked row exactly
	 * one immediate attempt. The attempt COUNT is untouched, so if that attempt fails `releasePending`
	 * computes the next delay from the same ladder and the row returns to the same escalated tier: the
	 * ladder is honored, only its clock is forgiven. A page load is a human action and cannot loop, so
	 * this is not a timer that can spin.
	 *
	 * Deliberately a discovery-filter bypass rather than a durable write: an extra IndexedDB pass on the
	 * startup path is one more thing that can stall before the drain ever runs, and it would have to be
	 * awaited to be useful.
	 */
	let pageLoadPacingBypassConsumed = false;
	// A reload intentionally gets a fresh claim epoch: it cannot match a prior-load row's
	// persisted claim. Same-owner orphan safety is decided by the liveness-lock probe.
	const startupOwnerClaimId = newUploadId();
	const schedulerOwnerId = `admin-drain:${startupOwnerClaimId}`;

	/**
	 * LSP-19088 — why a reload used to stall for the whole scheduler-lease TTL.
	 *
	 * `releaseScheduler` is gated on the origin being idle, on purpose: a leader with pending work
	 * keeps the lease so it can carry on draining rather than re-contending every pass. The cost is
	 * that a page which goes away mid-work leaves the lease held until it decays, and the next page
	 * load then declines with "another tab owns the durable upload scheduler" — for up to
	 * ADMIN_SCHEDULER_LEASE_TTL_MS, with a single tab open, precisely when the user reloaded to make a
	 * stuck upload go. Observed while validating LSP-19325: one tab, `retryAttempt` frozen, nothing
	 * ever picked the row up.
	 *
	 * `sessionStorage` is the one signal that separates "my own previous page load" from "another live
	 * tab" with certainty rather than by guessing at liveness: it is scoped to THIS tab and survives a
	 * reload, so an owner id found there was written by this tab before it navigated. Releasing that
	 * owner's lease is a handover from our past self, not a steal — `abandonScheduler` is fenced on
	 * owner identity, so it cannot touch any other tab's lease even if this read were wrong.
	 *
	 * Duplicating a tab copies `sessionStorage`, so the copy can name the original's live owner id.
	 * That is bounded and already handled: the original's next `renewScheduler` returns undefined,
	 * `markLost` aborts its controllers, and the lease changes hands. A handover, never two concurrent
	 * drains on the same rows.
	 */
	const SCHEDULER_OWNER_HANDOVER_KEY = "ls-contact-admin-upload-scheduler-owner";

	const schedulerOwnerIdFor = (claimId: string): string => `admin-drain:${claimId}`;
	/** The PREVIOUS page load's claim id, if this tab recorded one; a UUID (see fileUploadDb's owner-id rule). */
	const readPreviousSchedulerClaimId = (): string | undefined => {
		try {
			const stored = globalThis.sessionStorage?.getItem(SCHEDULER_OWNER_HANDOVER_KEY) ?? undefined;
			return stored && stored !== startupOwnerClaimId ? stored : undefined;
		} catch {
			// A blocked or unavailable sessionStorage costs only the handover shortcut; the lease still
			// decays on its own TTL.
			return undefined;
		}
	};

	/** The recorded claim id as stored, own or not — for diagnostics that must not hide the own-id case. */
	const readRecordedSchedulerClaimId = (): string | undefined => {
		try {
			return globalThis.sessionStorage?.getItem(SCHEDULER_OWNER_HANDOVER_KEY) ?? undefined;
		} catch {
			return undefined;
		}
	};

	const rememberSchedulerClaimId = (): void => {
		try {
			globalThis.sessionStorage?.setItem(SCHEDULER_OWNER_HANDOVER_KEY, startupOwnerClaimId);
		} catch {
			// Same bounded cost as the read above.
		}
	};

	/**
	 * Fable review of LSP-19325: duplicating a tab copies `sessionStorage`, so the copy reads the
	 * ORIGINAL's claim id as "my previous page load" and would hand its live lease back — the original
	 * learns at its next heartbeat, `markLost` aborts its in-flight controllers, and its uploads restart
	 * in the copy. Bounded, but pre-fix duplicating a tab was harmless.
	 *
	 * A Web Lock cannot outlive its page, which is exactly the property the sessionStorage signal
	 * lacks. Each page load holds a never-resolving lock for its own claim id for its lifetime; before
	 * abandoning a previous claim, probe that claim's lock — held means the owner is a LIVE page (a
	 * duplicate), so skip; free means it is genuinely gone. Not the steal-on-stale-heartbeat Jake
	 * rejected: it never judges a lease by time, only by whether the page that named it still exists.
	 *
	 * Both go through fileUploadDb's owner-liveness helpers rather than calling the Web Locks API
	 * here: that API is baselined Safari/iOS-15 compatibility debt at exactly one call site, and the
	 * support-floor gate refuses a second. The claim id is a UUID, which is what those helpers require.
	 * Only a HELD lock proves a live page. `isUploadOwnerLockFree` answers `false` when there is no
	 * Web Locks manager at all (Safari/iOS 15), which for its ownership callers means "assume held" but
	 * here would block every handover and wait out the full lease TTL (Bugbot, PR #4603). So the probe
	 * is consulted only where a lock manager exists; without one, or when the probe cannot tell (null),
	 * the owner is "not provably alive", the handover proceeds, and it stays owner-identity-fenced.
	 */
	const isPreviousSchedulerOwnerStillAlive = async (claimId: string): Promise<boolean> =>
		hasUploadOwnerLockManager() && (await isUploadOwnerLockFree(claimId)) === false;

	// Held so `dispose` can detach it by REFERENCE — asserting removal by event name alone passes even
	// on a real leak, so the listener identity has to be the thing that is kept and removed.
	let schedulerHandoverListener: (() => void) | undefined;

	/**
	 * Who holds the scheduler lease right now, for logs only. Every field a reload stall has needed to
	 * explain itself: the holder, whether it is this tab's own earlier load (a missed handover) or a
	 * stranger (a real second tab), and how long until it decays on its own.
	 */
	const describeSchedulerLease = async (ownership: DurableOwnershipPort | undefined): Promise<Record<string, unknown>> => {
		const recordedClaimId = readRecordedSchedulerClaimId();
		const base = {
			ownerId: schedulerOwnerId,
			recordedClaimId,
			recordedClaimIsOwn: recordedClaimId === startupOwnerClaimId
		};
		if (!ownership?.readSchedulerLease) return { ...base, lease: "unreadable" };
		try {
			const lease = await ownership.readSchedulerLease("contact-admin");
			if (!lease) return { ...base, lease: "none" };
			return {
				...base,
				leaseOwnerId: lease.ownerId,
				leaseEpoch: lease.epoch,
				leaseExpiresInMs: lease.expiresAt - Date.now(),
				leaseHeldByThisTabsEarlierLoad: recordedClaimId !== undefined && lease.ownerId === schedulerOwnerIdFor(recordedClaimId)
			};
		} catch (error) {
			return { ...base, lease: "read-failed", ...uploadFailureLogDetails(error) };
		}
	};
	const abandonSchedulerLease = async (ownerId: string, reason: "previous-page-load" | "page-hidden"): Promise<boolean> => {
		try {
			const ownership = await getAdminDurableOwnershipForCoordinator();
			if (!ownership?.abandonScheduler) return true;
			const outcome = await ownership.abandonScheduler({ originKey: "contact-admin", ownerId });
			if (outcome.kind === "applied") {
				console.info("[Upload] Released a scheduler lease its owner can no longer drain", { ownerId, reason });
			} else {
				// "skipped-not-owned" is also settled: there is nothing of that owner's left to release. It is
				// still logged WITH the holder — a handover that finds a stranger's lease here is the whole
				// difference between "already released" and "the lease was never this owner's to give back".
				console.info("[Upload] Scheduler handover found nothing of that owner's to release", {
					handedOverOwnerId: ownerId,
					reason,
					...(await describeSchedulerLease(ownership))
				});
			}
			return true;
		} catch (error) {
			// Silent-failure rule: a refused handover is why a reload would appear to do nothing, so it
			// must not be the one step that leaves no trace.
			console.warn("[Upload] Could not release the scheduler lease for a departed owner", {
				ownerId,
				reason,
				...uploadFailureLogDetails(error)
			});
			return false;
		}
	};
	/**
	 * LSP-19609: which of these pending rows may THIS drain act on? Excludes rows this tab's own dialog
	 * still intends to upload and rows a live lane in another tab intends (the same two exclusions
	 * `discoverPending` applies). Lane liveness is probed once per distinct lane and cached on the pass
	 * context so `discoverPending` does not probe the same lanes again.
	 */
	const findActionablePendingSessions = async (
		context: AdminUploadDrainContext,
		pending: readonly FileUploadSession[]
	): Promise<FileUploadSession[]> => {
		const dialog = host.$refs.fileUploadDialog;
		const unpinned = pending.filter((session) => dialog?.ownsQueuedUploadSession?.(session.id, session.uploadId) !== true);
		const cache = (context.laneIntentLivenessByLaneId ??= new Map());
		const now = Date.now();
		const distinctLaneIds = [
			...new Set(unpinned.map((session) => session.laneIntentId).filter((laneId): laneId is string => laneId !== undefined))
		];
		await Promise.all(
			distinctLaneIds
				.filter((laneId) => !cache.has(laneId))
				.map(async (laneId) => {
					const representative = unpinned.find((session) => session.laneIntentId === laneId)!;
					try {
						cache.set(laneId, await getFileUploadLaneIntentLiveness(representative, now));
					} catch (error) {
						// The probe already fails toward "dead" internally; this is the belt for its braces. A
						// probe that still rejects must not turn into "skip the whole pass" under the enclosing
						// catch's "Durable scheduler unavailable" banner (AI review, PR #4812): treat the lane as
						// not provably alive — the same take-rather-than-strand bias the probe itself has.
						console.warn(
							"[Upload] Lane-intent liveness probe failed in the actionability preflight; treating the lane as not alive",
							{
								laneIntentId: laneId,
								...uploadFailureLogDetails(error)
							}
						);
						cache.set(laneId, "dead");
					}
				})
		);
		return unpinned.filter((session) => session.laneIntentId === undefined || cache.get(session.laneIntentId) !== "alive");
	};
	// Latched on the shape of the skip, not per pass: the recovery wake re-runs this every few seconds
	// while the sibling batch runs, and one line per distinct state is a diagnostic, one per pass is noise.
	let lastNothingActionableSignature: string | undefined;
	const reportNothingActionable = (pending: readonly FileUploadSession[]) => {
		const dialog = host.$refs.fileUploadDialog;
		const pinned = pending.filter((session) => dialog?.ownsQueuedUploadSession?.(session.id, session.uploadId) === true);
		const drivenByLiveLaneElsewhere = pending.filter((session) => !pinned.includes(session));
		// Latched on WHICH kinds of row are holding the drain back, not on how many: during a sibling
		// batch the pending count changes on every pass, and a line per change is a 900-line record for
		// a 900-file batch (observed on the validation run — one line per count step). The counts in the
		// payload are those of the first pass that entered this shape.
		const signature = `${pinned.length > 0 ? "pinned" : "-"}:${drivenByLiveLaneElsewhere.length > 0 ? "foreign" : "-"}`;
		if (signature === lastNothingActionableSignature) return;
		lastNothingActionableSignature = signature;
		console.info(
			"[Upload] Not taking the upload scheduler: every pending row belongs to a live lane; nothing for this tab's drain to do",
			{
				ownerId: schedulerOwnerId,
				pendingCount: pending.length,
				pinnedByThisTabsDialog: pinned.length,
				drivenByLiveLaneElsewhere: drivenByLiveLaneElsewhere.length,
				// The rows a live foreign lane is driving — same list discoverPending's own skip log carried,
				// bounded so a thousand-row sibling batch does not become a thousand-line record.
				sessionIds: drivenByLiveLaneElsewhere.slice(0, 50).map((session) => session.id)
			}
		);
	};
	/**
	 * LSP-19766: `resetUploadingSessions`'s callback for a row it is deliberately holding back this
	 * pass (heartbeat not yet stale — see fileUploadDb.ts). Reported sessions accumulate into
	 * `reportedThisPass` so `reconcileVerifyingOrphanSessions` can diff against the previous pass.
	 *
	 * Bugbot (LSP-19766): this used to skip the dialog call whenever `verifyingOrphanSessionIds`
	 * already listed the session, to avoid a redundant call. That skip fired even on a pass where the
	 * PRIOR call never actually reached the dialog (tray unmounted yet) — `reportedThisPass.add`
	 * still ran, so the next pass's reconciliation treated the session as already shown and the
	 * indicator never appeared for the rest of the wait, matching nearby code's own rule that a
	 * missing-tray miss must not consume one-shot state (`surfaceTerminallyParkedUploads`'s "the tray
	 * is unavailable" warning does not mark anything as delivered either). The dialog's own
	 * `showVerifyingOrphanedUpload` is already idempotent — a repeat call for a session already shown
	 * as verifying just re-asserts the same flag — so there is no correctness reason to skip it; the
	 * call is now made every pass the session is reported, unconditionally.
	 */
	const reportOrphanAwaitingHeartbeat =
		(reportedThisPass: Set<string>) =>
		(session: FileUploadSession): void => {
			reportedThisPass.add(session.id);
			const dialog = host.$refs.fileUploadDialog;
			if (typeof dialog?.showVerifyingOrphanedUpload !== "function") {
				console.warn("[Upload] Could not surface an orphaned upload awaiting heartbeat staleness: the upload tray is unavailable", {
					sessionId: session.id
				});
				return;
			}
			dialog.showVerifyingOrphanedUpload({
				sessionId: session.id,
				fileName: session.fileName,
				fileSize: session.fileSize,
				fileType: session.fileType
			});
		};
	/** LSP-19766: clears any previously-reported session this pass did not report again. */
	const reconcileVerifyingOrphanSessions = (reportedThisPass: Set<string>): void => {
		const dialog = host.$refs.fileUploadDialog;
		for (const sessionId of verifyingOrphanSessionIds) {
			if (reportedThisPass.has(sessionId)) continue;
			if (typeof dialog?.clearVerifyingOrphanedUpload === "function") dialog.clearVerifyingOrphanedUpload(sessionId);
		}
		verifyingOrphanSessionIds = reportedThisPass;
	};
	const runCancelledUploadDrain = async () => {
		if (disposed) return;
		if (!cancelledDrainInFlight) {
			cancelledDrainInFlight = drainCancelledAdminUploadSessions({ shouldContinue: () => !disposed })
				.then(() => undefined)
				.finally(() => {
					cancelledDrainInFlight = undefined;
				});
		}
		await cancelledDrainInFlight;
	};
	const reportBridgeAdmissionFailure = (error: unknown) => {
		console.warn("[Upload] Upload bridge admission unavailable; drain deferred for retry", uploadFailureLogDetails(error));
		// Keep the engine alive and use the existing bounded recovery ladder. In particular,
		// startup admission failures must not unwind engine.start(), which would stop all lazy
		// wake timers and strand pending multipart rows until a full reload.
		wakePolicy?.scheduleRecovery();
	};
	const runStartupReclaim = async () => {
		// A disposed drain is terminal: it must not start admission or reclaim, and must leave the
		// shared startup flag untouched so a replacement drain can perform its own reclaim.
		if (disposed) return;
		// Admission must precede discovery and reclaim; a failed bridge leaves bytes untouched for
		// recovery. Do not let a transient bridge failure escape engine.start(): its wake policy is
		// the mechanism that retries admission without requiring a reload.
		try {
			await ensureAdminUploadBridgeReady();
			bridgeReady = true;
		} catch (error) {
			bridgeReady = false;
			reportBridgeAdmissionFailure(error);
			return;
		}
		// Before admission, not after: the whole point is that this page load can CLAIM the scheduler,
		// and the claim happens inside the drain pass this reclaim leads into.
		const previousClaimId = readPreviousSchedulerClaimId();
		let handoverSettled = true;
		if (!previousClaimId) {
			// Silent-failure rule: "no handover ran" is indistinguishable from "handover ran and worked"
			// unless the no-op says so. Own id here means this is a warm-up re-run, not a fresh load.
			const recordedClaimId = readRecordedSchedulerClaimId();
			if (recordedClaimId !== startupOwnerClaimId) {
				console.info("[Upload] No previous scheduler owner recorded for this tab; nothing to hand over", {
					ownerId: schedulerOwnerId,
					recordedClaimId
				});
			}
		}
		if (previousClaimId) {
			if (await isPreviousSchedulerOwnerStillAlive(previousClaimId)) {
				// A duplicated tab: the "previous" owner is a live sibling. Its lease is its own.
				console.info("[Upload] Previous scheduler owner is a live page (duplicated tab); leaving its lease alone", {
					previousOwnerId: schedulerOwnerIdFor(previousClaimId),
					ownerId: schedulerOwnerId
				});
			} else {
				handoverSettled = await abandonSchedulerLease(schedulerOwnerIdFor(previousClaimId), "previous-page-load");
			}
		}
		// Bugbot (PR #4603): the previous claim id is the ONLY handle that can release a departed owner's
		// non-idle lease. Overwriting it after a handover that THREW (an IndexedDB hiccup at startup)
		// stranded the lease until its TTL: every later reload found only this load's own id. Keep the
		// previous id when the handover did not settle so the next load retries it; this load's own lease
		// still decays on its TTL and is released on pagehide.
		if (handoverSettled) rememberSchedulerClaimId();
		else
			console.warn("[Upload] Keeping the previous scheduler owner recorded so the next page load retries its handover", {
				previousClaimId
			});
		ensureUploadOwnerLivenessLockHeldForOwner(startupOwnerClaimId);
		// The unload half: gives the lease up immediately when it works, so the next load does not even
		// need the startup handover. `pagehide` rather than `beforeunload` because it also fires when the
		// page enters the back/forward cache, and a bfcache-frozen page cannot drain either. Re-entering
		// via `pageshow` is already a wake trigger, so the lease is simply re-acquired then.
		if (!schedulerHandoverListener && typeof window !== "undefined") {
			schedulerHandoverListener = () => void abandonSchedulerLease(schedulerOwnerId, "page-hidden");
			window.addEventListener("pagehide", schedulerHandoverListener);
		}
		await runCancelledUploadDrain();
		// The startup orphan sweep runs exactly once per page load. It is gated ONLY by the once-flag,
		// never by whether this page happens to have an upload in flight.
		//
		// Two authorities previously met here and appeared to conflict; they are reconciled as follows.
		//
		// (1) The prior break-it round added a coarse `hasCurrentPageInitiatedFileUpload()` bail — both
		//     at this entry point and again immediately before the destructive write — because "the
		//     admission check only proves 'idle' at the moment this function was ENTERED. A sibling
		//     openDialog/persist path can start a brand-new upload on this same tab while we are still
		//     only queued to run ... otherwise a stale 'idle' read would reclaim the row that sibling
		//     path just created for an upload actively in flight, flipping it to 'pending' and exposing
		//     it to the resume drain mid-upload." That intent is correct and is PRESERVED — but not by
		//     this bail.
		// (2) It is preserved by the per-row discrimination one layer down: `resetUploadingSessions`
		//     spares any row for which `hasCurrentPageInitiatedFileUpload() && isCurrentPageInitiated-
		//     FileUploadSession(session)` holds (fileUploadDb.ts `isCurrentPageAdmittedRow`, documented
		//     there as "A row this page admitted is always protected, whether or not
		//     resetCurrentTabOwnedRows is true"). Both halves of that check are same-realm in-memory
		//     module state, and the per-row identity is marked inside the SAME IDB transaction that puts
		//     the row (`createFileUploadSessionWithOutcome`). So a row that is visible to the sweep's
		//     candidate scan is, by construction, already in the identity map: the read can never be
		//     stale for a row this page created — including one created during the
		//     `ensureAdminUploadBridgeReady()` / `runCancelledUploadDrain()` awaits above.
		//
		// Reconciliation: the coarse bail's only effect that the per-row guard does not already provide
		// was abandoning the ENTIRE orphan sweep whenever any upload happened to start during those
		// awaits — stranding unrelated prior-load orphans (delayed recovery of recoverable bytes) with
		// no compensating safety, since the live row was already protected per-row. Operation Upload
		// inserted those two awaits ahead of the gate and so made that mis-bail the common case.
		// Removed per Lane I §4.4 + orchestrator ruling 2026-08-21.
		if (host._uploadStartupReclaimDone) return;
		try {
			await resetUploadingSessions(true, startupOwnerClaimId);
			// The reset may resolve after disposal. Do not renew shared liveness, mark the host reclaim
			// flag, or start another shared IndexedDB mutation from a stale drain.
			if (disposed) return;
			ensureUploadOwnerLivenessLockHeld();
			skipRecurringReset = true;
			host._uploadStartupReclaimDone = true;
			await drainCancelledUploadSessions();
			await runCancelledUploadDrain();
		} catch (error) {
			if (error instanceof IdbStallError) return;
			if (isIdbInternalError(error))
				return console.error(
					"[Upload] IDB internal error during startup reset — upload resume disabled this session",
					uploadFailureLogDetails(error)
				);
			console.warn("[Upload] Startup upload reclaim failed — warm-up passes will retry", uploadFailureLogDetails(error));
		}
	};
	// R-0 timed wake. One outstanding one-shot at a time, always the EARLIEST due instant, so a paced
	// session is retried the moment its backoff elapses instead of waiting for the two-minute poll —
	// and a long window (up to fifteen minutes) is honored exactly rather than being spun on.
	let dueDrainTimer: ReturnType<typeof setTimeout> | undefined;
	let dueDrainAt: number | undefined;
	const clearDueDrain = (): void => {
		if (dueDrainTimer !== undefined) globalThis.clearTimeout(dueDrainTimer);
		dueDrainTimer = undefined;
		dueDrainAt = undefined;
	};
	const scheduleDueDrain = async (context: AdminUploadDrainContext): Promise<void> => {
		if (disposed) return;
		// The pass just read every pending row; fold THAT snapshot instead of scanning `status:"pending"`
		// a second time for the same rows. It is not stale for the rows this pass touched: the driver's
		// `releasePending` (adminSessionStore) writes the new `retryAt` to IDB and then `Object.assign`s
		// it back onto the very session object this array holds, so a row deferred during the pass folds
		// with its POST-pass window. Only a discovery that bailed before the read (scheduler lease lost,
		// or an IDB error) has no snapshot, and only that case still pays for a scan.
		//
		// A row that COMPLETED or was cancelled during the pass can still be in the snapshot carrying a
		// future `retryAt`, which at worst arms one extra wake: that pass rediscovers nothing, folds an
		// empty snapshot, and arms nothing further — so it cannot spin.
		const remainingParkedRequeueDelayMs = (): number | undefined => {
			if (context.parkedRequeueDeadlineAt === undefined) return undefined;
			if (!Number.isFinite(context.parkedRequeueDeadlineAt)) return 0;
			return Math.max(0, context.parkedRequeueDeadlineAt - Date.now());
		};
		const now = Date.now();
		const parkedRequeueDelayMs = remainingParkedRequeueDelayMs();
		let delayMs =
			context.pendingSessionSnapshot === undefined
				? await computeNextFileUploadDrainDelayMs(parkedRequeueDelayMs)
				: computeFileUploadDrainDelayMsFor(context.pendingSessionSnapshot, now, parkedRequeueDelayMs);
		if (context.pendingSessionSnapshot === undefined) {
			const remainingParkedDelayMs = remainingParkedRequeueDelayMs();
			if (remainingParkedDelayMs !== undefined) {
				delayMs = delayMs === undefined ? remainingParkedDelayMs : Math.min(delayMs, remainingParkedDelayMs);
			}
		}
		if (disposed || delayMs === undefined) return;
		const dueAt = Date.now() + delayMs;
		// Keep the nearer of the two: a later pass must not push an already-armed sooner wake out.
		if (dueDrainTimer !== undefined && dueDrainAt !== undefined && dueDrainAt <= dueAt) return;
		clearDueDrain();
		dueDrainAt = dueAt;
		dueDrainTimer = globalThis.setTimeout(
			() => {
				dueDrainTimer = undefined;
				dueDrainAt = undefined;
				if (disposed) return;
				void engineRef.current
					?.drainNow("r0-retry-due")
					.catch((error) => console.warn("[Upload] Timed upload retry wake failed", uploadFailureLogDetails(error)));
			},
			// setTimeout saturates past ~24.8 days; every R-0 window is minutes, but clamp anyway.
			Math.min(Math.max(delayMs, 1), MAX_DUE_DRAIN_DELAY_MS)
		);
	};
	const wakePolicy = createAdminUploadWakePolicy(host, runStartupReclaim, rearmSmallFileUploads);
	// In-flight discovery or claiming can finish after disposal and invoke the wake policy. Guard
	// each callback so a stale drain cannot alter the shared recovery timer; lifecycle start/stop
	// remain owned by the engine.
	const engineWakePolicy: typeof wakePolicy = {
		...wakePolicy,
		pendingObserved: () => {
			if (disposed) return;
			wakePolicy.pendingObserved();
		},
		idleObserved: (observedAt) => {
			if (disposed) return;
			wakePolicy.idleObserved(observedAt);
		},
		claimMissed: () => {
			if (disposed) return;
			wakePolicy.claimMissed();
		}
	};
	const engine = createUploadDrainEngine<FileUploadSession, AdminUploadDrainContext>({
		wakePolicy: engineWakePolicy,
		resumeConcurrency: FILE_UPLOAD_CONCURRENCY,
		reclaimStartup: runStartupReclaim,
		beforeDrain: async () => {
			if (!bridgeReady) {
				try {
					await ensureAdminUploadBridgeReady();
					bridgeReady = true;
				} catch (error) {
					bridgeReady = false;
					reportBridgeAdmissionFailure(error);
					return {
						controllers: new Map(),
						counts: { resumed: 0, failed: 0, lost: 0, handedOff: 0, needsAttention: 0, recoveryBlocked: 0, pendingRetry: 0 },
						hasDiscoveredPending: false,
						retryingIds: new Set(),
						verificationPendingIds: new Set(),
						failedIds: new Set(),
						recoveryBlockedIds: new Set(),
						schedulerLost: false,
						bridgeReady: false
					};
				}
			}
			await runCancelledUploadDrain();
			return {
				controllers: new Map(),
				counts: { resumed: 0, failed: 0, lost: 0, handedOff: 0, needsAttention: 0, recoveryBlocked: 0, pendingRetry: 0 },
				hasDiscoveredPending: false,
				retryingIds: new Set(),
				verificationPendingIds: new Set(),
				failedIds: new Set(),
				recoveryBlockedIds: new Set(),
				schedulerLost: false,
				bridgeReady: true
			};
		},
		async claimDrain(context, run) {
			if (!context.bridgeReady) {
				// Admission failure is a claim miss, not an idle result. The wake policy keeps a
				// bounded retry timer alive while durable rows remain untouched.
				return undefined;
			}
			// Without Web Locks, an orphan can still be left in `uploading` after the
			// previous leader dies. Reclaim it before durable scheduler admission so the
			// scheduler sees the resulting pending work and owns the whole drain pass.
			// The reset itself is heartbeat/liveness- and writer-fence-guarded.
			if (!navigator.locks) await resetUploadingSessions(false);
			const claimWithWebLock = async () => {
				if (!navigator.locks) return run();
				return navigator.locks.request("ls-contact-upload-resume", { ifAvailable: true }, async (lock) => {
					if (!lock) {
						console.info("[Upload] Another tab is already resuming uploads, skipping");
						return undefined;
					}
					return run();
				});
			};
			let ownership: Awaited<ReturnType<typeof getAdminDurableOwnershipForCoordinator>>;
			try {
				ownership = await getAdminDurableOwnershipForCoordinator();
			} catch (error) {
				console.warn(
					"[Upload] Durable ownership coordinator unavailable; upload drain deferred for retry",
					uploadFailureLogDetails(error)
				);
				return undefined;
			}
			if (!ownership) return claimWithWebLock();
			let schedulerToken: DurableLeaseToken | undefined;
			try {
				const allPending = await getPendingFileUploadSessions();
				// LSP-22950 O8: rows of another organization/user are not this session's work; they must not take the scheduler.
				const pending = holdOutOfScopeSessions(allPending);
				if (pending.length === 0) {
					context.maintenanceOnly = true;
					return run();
				}
				// LSP-19609: do not take (or renew) the scheduler for work this drain will only skip.
				//
				// `acquireScheduler` by the current holder RENEWS its lease, and the idle-gated release
				// (kept exactly as it is, below — "idle" there is the origin-global truth the shared port
				// defines) never fires while any pending row exists in the shared database. So a tab whose
				// only pending rows were pinned by its own dialog or intended by a live lane in another tab
				// (LSP-19537) held the scheduler for the life of its page: every wake re-acquired, every pass
				// skipped every row, and the tab whose lane had just yielded six rows to "the background" was
				// refused the scheduler ~1000 times over 30 minutes while nothing anywhere picked them up.
				//
				// Deciding BEFORE acquisition is the lever that needs no new lease semantics: a holder with
				// nothing actionable simply stops renewing, its lease decays on the ordinary TTL, and the next
				// tab with actionable rows acquires. A drain that does have actionable rows behaves exactly as
				// before. Reported as a missed claim so the existing recovery wake keeps polling, the same way
				// it does when another tab holds the lease.
				if (typeof host.$refs.fileUploadDialog?.openForResumedUploads !== "function") {
					// No dialog to drive a resume through: discoverPending would only log "dialog unavailable"
					// and return nothing, yet the acquisition would still renew the lease over every pending
					// row (Codex diff review). Same missed-claim path; the recovery wake retries once it mounts.
					if (lastNothingActionableSignature !== "no-dialog") {
						lastNothingActionableSignature = "no-dialog";
						console.info("[Upload] Not taking the upload scheduler: no upload dialog is mounted to drive a resume", {
							ownerId: schedulerOwnerId,
							pendingCount: pending.length
						});
					}
					return undefined;
				}
				const actionable = await findActionablePendingSessions(context, pending);
				if (actionable.length === 0) {
					reportNothingActionable(pending);
					return undefined;
				}
				lastNothingActionableSignature = undefined;
				await ownership.noteWork({ originKey: "contact-admin", pendingCount: pending.length, hasDueWork: true });
				schedulerToken = await ownership.acquireScheduler({
					originKey: "contact-admin",
					ownerId: schedulerOwnerId,
					ttlMs: ADMIN_SCHEDULER_LEASE_TTL_MS
				});
			} catch (error) {
				// A durable scheduler failure is an unavailable ownership decision, not a
				// migration signal. Returning undefined lets the drain engine mark the claim
				// missed and use its existing recovery wake policy while queued bytes remain
				// untouched. The Web Lock path is retained only for explicit legacy-mode
				// compatibility when no durable coordinator is configured.
				console.warn("[Upload] Durable scheduler unavailable; upload drain deferred for retry", uploadFailureLogDetails(error));
				return undefined;
			}
			if (!schedulerToken) {
				// Not necessarily another tab: an unexpired lease left by this tab's own earlier load (a
				// handover that did not land) reads exactly the same from here. Say which, and for how long.
				console.warn(
					"[Upload] Could not acquire the durable upload scheduler; skipping this pass",
					await describeSchedulerLease(ownership)
				);
				return undefined;
			}
			try {
				return await runWithSchedulerHeartbeat(context, ownership, schedulerToken, run);
			} finally {
				const remaining = holdOutOfScopeSessions(await getPendingFileUploadSessions());
				await ownership.noteWork({
					originKey: "contact-admin",
					pendingCount: remaining.length,
					hasDueWork: remaining.length > 0
				});
				await ownership.releaseScheduler({
					token: schedulerToken,
					isIdle: async () => holdOutOfScopeSessions(await getPendingFileUploadSessions()).length === 0
				});
			}
		},
		async discoverPending(context) {
			if (context.schedulerLost) return [];
			const isFirstDiscovery = !context.hasDiscoveredPending;
			context.hasDiscoveredPending = true;
			// `context` is rebuilt per PASS, so its own first-discovery flag cannot express "first
			// discovery of this page load" — that has to live on the drain instance, which is created
			// once per load.
			const bypassRetryPacingThisPass = !pageLoadPacingBypassConsumed;
			// LSP-19766: collected here (not inside the `if` below) and reconciled in `finally`, AFTER
			// the rest of this pass — including the `dialog.openForResumedUploads(...)` call further
			// down for a row this SAME pass just reclaimed. Bugbot: reconciling immediately after the
			// reset call closed the tray for a session that was about to be re-opened moments later in
			// this very pass — the exact happy-path moment a reclaim succeeds — producing a visible
			// close/reopen flash right when the UI should look reassuring, not glitchy.
			const orphanSessionIdsThisPass = new Set<string>();
			let ranOrphanAwaitingHeartbeatScan = false;
			try {
				// Durable scheduler ownership is the no-Web-Locks fallback, but it does not
				// change the app-row status left by a killed leader. Re-run the orphan scan
				// on every successor pass so an expired/stale `uploading` row is returned to
				// the pending queue even when navigator.locks is unavailable. The reset
				// function still applies its heartbeat/liveness proof and writer fence.
				if (!skipRecurringReset) {
					await resetUploadingSessions(false, undefined, reportOrphanAwaitingHeartbeat(orphanSessionIdsThisPass));
					ranOrphanAwaitingHeartbeatScan = true;
				}
				skipRecurringReset = false;
				// R-0 (owner directive 2026-08-26): the RUNNING app returns parked sessions to the pool,
				// not just the startup pass (F-17). This is the recurring half `resetUploadingSessions`
				// deliberately does not do, and it is safe here only because it is paced — see
				// `requeueNeedsAttentionFileUploadSessions` for the LSP-18742 spin this must not become.
				context.parkedRequeueDeadlineAt = (await requeueNeedsAttentionFileUploadSessions()).nextRetryAt;
				const now = Date.now();
				void sweepStaleLaneLeasesIfDue(now);
				// The snapshot keeps the rows the two filters below exclude from THIS pass — already-controlled,
				// or still inside their `retryAt` window — because `afterDrain`'s timed wake must still
				// consider them; the `retryAt` ones are precisely what a wake exists for. Stashing it is what
				// lets `scheduleDueDrain` skip re-scanning the same rows.
				//
				// The dialog-owned exclusion below deliberately does NOT move up here. A break-it round claimed
				// ~1000 excluded-but-due rows would make `scheduleDueDrain` wake immediately and spin, but that
				// round mocked `computeFileUploadDrainDelayMsFor` to a constant 0; the real one skips every row
				// whose `retryAt` is not a number, and `finishPrePersist` parks these rows with `retryAt: null`.
				// So they contribute no wake either way, and leaving them in the snapshot keeps this read doing
				// exactly what its contract says.
				// `context.controllers` only knows the rows THIS drain is driving; it says nothing about the rows
				// the dialog's own batch is queued for. Pre-persist parks every one of those as unowned
				// `pending` the moment its bytes land, long before the batch reaches them — ~20 rows of a
				// 23-file batch, for as long as the batch runs. That is how two lanes ended up on one row:
				// refused claims, a server-side supersede that aborted the live S3 multipart upload, and 404
				// NoSuchUpload on the survivor's in-flight part PUTs (2026-09-01 — 13 of 23 never delivered).
				//
				// Asked through the dialog rather than a module flag on purpose: the answer has to be "a LIVE
				// lane intends this row", not "this page created this row". The dialog clears the flag as it
				// starts each upload and in a `finally` around the whole batch, so the exclusion cannot outlive
				// the work it protects. The optional call keeps a host with no mounted dialog — and every drain
				// spec whose dialog facade predates this — behaving exactly as before.
				// LSP-22950 O8: held rows stay out of the snapshot too, so a due-drain wake does not spin on work this session
				// will never take; they are re-read on every pass and resume once the scope matches.
				const pendingSessions = holdOutOfScopeSessions(await getPendingFileUploadSessions());
				context.pendingSessionSnapshot = pendingSessions;
				// LSP-19384: rows the app has stopped retrying on its own are invisible to everything
				// below — that snapshot is `pending` only. Started here, unawaited, so it runs on every
				// pass INCLUDING the "nothing pending" early return, which is the common case for a tray
				// that holds only parked rows.
				//
				// Deliberately NOT awaited. It is a presentation read, and putting one on the critical
				// path would make real upload work wait behind it — the extra IndexedDB round trip alone
				// was enough to push six existing drain specs past their timeout. It also cannot reject
				// into the caller: the helper owns its own try/catch, so a failed read never becomes an
				// unhandled rejection or stops the drain from doing actual work.
				void surfaceTerminallyParkedUploads(host, () => !disposed);
				if (context.maintenanceOnly) return [];
				const sessions = pendingSessions
					.filter((session) => !context.controllers.has(session.id))
					.filter((session) => host.$refs.fileUploadDialog?.ownsQueuedUploadSession?.(session.id, session.uploadId) !== true)
					// Admin has always WRITTEN a durable `retryAt` (adminSessionStore's releasePending) and
					// never read it, so every pending row was re-attempted every pass. Honoring it is what
					// turns R-0's escalating backoff into actual pacing rather than a stored number.
					//
					// Break-it round 1 (D7): `NaN <= now` is false forever, so one corrupt persisted number
					// hid a row from this filter permanently — an unschedulable park with nothing to show
					// for it. A retryAt that is not a finite instant carries no schedule, so it is due now.
					// See `pageLoadPacingBypassConsumed`: the first discovery of a page load forgives the
					// paced due-time so a refresh actually retries, and every later pass paces normally.
					.filter((session) => bypassRetryPacingThisPass || isFileUploadRetryDue(session, now));
				// LSP-19537: after the three filters above (controllers, same-tab dialog pin, retry
				// pacing), also drop any row a LIVE lane in a DIFFERENT tab still intends to upload. This
				// is what makes the dialog pin above cross-tab: same-tab intent is already decided by
				// ownsQueuedUploadSession; this asks the same question of every OTHER tab via the durable
				// lane lease. Every ambiguous verdict resolves to "dead" (take) — see
				// getFileUploadLaneIntentLiveness — so this can only ever skip a row this drain can prove
				// another tab is actively driving right now.
				const livenessByLaneIntentId = new Map<string, Promise<FileUploadLaneIntentLiveness>>();
				const getLaneIntentLiveness = (session: FileUploadSession): Promise<FileUploadLaneIntentLiveness> => {
					const laneIntentId = session.laneIntentId;
					if (laneIntentId === undefined) return getFileUploadLaneIntentLiveness(session, now);
					// LSP-19609: claimDrain's actionability preflight already probed this pass's lanes.
					const cachedVerdict = context.laneIntentLivenessByLaneId?.get(laneIntentId);
					if (cachedVerdict !== undefined) return Promise.resolve(cachedVerdict);
					let liveness = livenessByLaneIntentId.get(laneIntentId);
					if (liveness === undefined) {
						liveness = getFileUploadLaneIntentLiveness(session, now);
						livenessByLaneIntentId.set(laneIntentId, liveness);
					}
					return liveness;
				};
				const laneLivenessResults = await Promise.all(
					sessions.map(async (session) => ({ session, liveness: await getLaneIntentLiveness(session) }))
				);
				const laneExcluded = laneLivenessResults.filter(({ liveness }) => liveness === "alive").map(({ session }) => session.id);
				if (laneExcluded.length > 0) {
					console.info("[Upload] discoverPending skipped rows a live foreign lane still intends", {
						skippedCount: laneExcluded.length,
						sessionIds: laneExcluded
					});
				}
				const laneFilteredSessions = laneLivenessResults
					.filter(({ liveness }) => liveness !== "alive")
					.map(({ session }) => session);
				// A discovery started before disposal must not update shared announcement state.
				if (disposed) return laneFilteredSessions;
				if (sessions.length === 0) {
					// Nothing was held back, so the page load's one forgiven attempt has nothing to
					// forgive; consume it here so later passes pace normally.
					pageLoadPacingBypassConsumed = true;
					if (isFirstDiscovery) {
						lastAnnouncedRetrySessionIds = null;
						lastAnnouncedFailedSessionIds = null;
						lastAnnouncedRecoveryBlockedSessionIds = null;
						host._lastAnnouncedUploadRetrySessionIds = null;
						host._lastAnnouncedUploadFailedSessionIds = null;
					}
					return laneFilteredSessions;
				}
				// A live foreign lane held back every row, so this pass had no opportunity to use
				// the page load's forgiven attempt. Preserve it for the first pass that can act.
				if (laneFilteredSessions.length === 0) return laneFilteredSessions;
				await logStorageEstimate();
				// Check again after the storage-estimate await before mutating the dialog or controllers.
				if (disposed) return laneFilteredSessions;
				const dialog = host.$refs.fileUploadDialog;
				if (!dialog) {
					console.warn("[Upload] Pending upload discovery skipped: dialog unavailable", {
						sessionCount: laneFilteredSessions.length,
						sessionIds: laneFilteredSessions.map((session) => session.id)
					});
					return [];
				}
				// Consumed only once a discovery reached the read AND this pass can act on what it found:
				// a pass that bailed at admission, was disposed mid-flight, or had no dialog to drive must
				// leave the page load's one forgiven attempt for the pass that really runs (Fable review:
				// consuming it above the `!dialog` bail burned the attempt with nothing resumed).
				pageLoadPacingBypassConsumed = true;
				const resumedUploads = laneFilteredSessions.map((session) => ({
					sessionId: session.id,
					uploadId: session.uploadId,
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId,
					fileName: session.fileName,
					fileSize: session.fileSize,
					fileType: session.fileType
				}));
				if (context.controllers.size > 0) {
					dialog.openForResumedUploads(resumedUploads, new Set(context.controllers.keys()));
				} else {
					dialog.openForResumedUploads(resumedUploads);
				}
				for (const session of laneFilteredSessions) {
					const controller = new AbortController();
					context.controllers.set(session.id, controller);
					dialog.setResumedFileAbortController(session.id, controller);
				}
				return laneFilteredSessions;
			} catch (error) {
				if (isIdbInternalError(error)) {
					console.error(
						"[Upload] IDB internal error fetching pending sessions — skipping this cycle",
						uploadFailureLogDetails(error)
					);
					return [];
				}
				throw error;
			} finally {
				// Runs after the pass's own return value is computed — including a successful
				// `dialog.openForResumedUploads(...)` call above for a row this pass just reclaimed —
				// so a reclaim-and-reopen never races the clear-and-close below.
				if (ranOrphanAwaitingHeartbeatScan && !disposed) reconcileVerifyingOrphanSessions(orphanSessionIdsThisPass);
			}
		},
		async resumeSession(session, context) {
			if (disposed || context.schedulerLost) return;
			const signal = context.controllers.get(session.id)!.signal;
			if (signal.aborted) return;
			// LSP-19543: from here on the pass is actually DRIVING this session, not merely discovering
			// it, so the foreground dialog's success toast must see the tab as busy. The `finally`
			// releases the claim on every exit — an outcome branch, an abort, disposal, or a throw the
			// engine's `resumeOne` routes to `onSessionError`.
			beginBackgroundUploadAttempt(session.id);
			try {
				const dialog = host.$refs.fileUploadDialog;
				let result: unknown;
				await withFileUploadConcurrencySlot(async () => {
					if (disposed || signal.aborted) return;
					if (holdOutOfScopeSessions([session]).length === 0) return;
					result = await host.resumeMultipartSession(
						session,
						(bytesUploaded: number) => {
							// Architecture-gap round 2: an already-in-flight resume can report progress (or
							// resolve at all) after this instance was disposed — a replacement instance's row
							// for the same session must not be mutated by a disposed instance's stale callback.
							if (disposed) return;
							dialog.updateResumedFileProgress(session.id, bytesUploaded);
						},
						signal
					);
				});
				// Architecture-gap round 2: the shared engine's resumeBatch only checks its OWN internal
				// `disposed` before STARTING a new resumeOne call; it never re-checks after an
				// already-in-flight resumeSession's awaited call resolves. Without this guard, a disposed
				// instance's late completion would still mutate `context` (harmless — instance-local and
				// afterDrain is skipped once disposed) AND the shared dialog (not harmless — it can
				// overwrite a replacement instance's completed/failed row back to retrying, or vice versa).
				if (disposed || signal.aborted) return;
				if (result === RESUME_OUTCOME.COMPLETED) {
					dialog.markResumedFileComplete(session.id);
					context.counts.resumed++;
				} else if (result === RESUME_OUTCOME.HANDED_OFF) {
					dialog.markResumedFileHandedOff(session.id);
					context.counts.handedOff++;
				} else if (result === RESUME_OUTCOME.LOST) {
					dialog.markResumedFileFailed(session.id, LOST_MESSAGE);
					context.counts.lost++;
				} else if (result === RESUME_OUTCOME.NEEDS_ATTENTION) {
					dialog.updateResumedFileIdentity(session);
					// `markResumedFileNeedsAttention`, not `markResumedFileFailed`: red and the header's
					// "N uploads failed" are reserved for rows the CUSTOMER has to act on because something
					// they supplied was wrong. A parked row is a good file whose server-side upload target
					// went away underneath it — nobody did anything wrong — so it gets warning tone and its
					// own header bucket. See the dialog's `needsAttention` flag.
					dialog.markResumedFileNeedsAttention(
						session.id,
						stoppedUploadMessage(session.lastError, !isUploadTerminalFailure(session.lastError))
					);
					context.counts.needsAttention++;
				} else if (result === RESUME_OUTCOME.RECOVERY_BLOCKED) {
					dialog.updateResumedFileIdentity(session);
					dialog.markResumedFileNeedsAttention(session.id, RECOVERY_BLOCKED_ROW_MESSAGE, { recoveryBlocked: true });
					context.counts.recoveryBlocked++;
					context.recoveryBlockedIds.add(session.id);
				} else if (result === RESUME_OUTCOME.RETRY) {
					if (isUploadVerificationPending(session.lastError)) {
						dialog.markResumedFileFinishing(session.id);
						context.verificationPendingIds.add(session.id);
					} else {
						if (session.targetPending) dialog.markResumedFileRetrying(session.id, uploadStartRetryMessage("this upload"));
						else dialog.markResumedFileRetrying(session.id);
						context.retryingIds.add(session.id);
						context.counts.pendingRetry++;
					}
				} else {
					// Silent-failure rule: this fall-through had NO message and NO log at all, so an outcome
					// the mapping above does not know about rendered as a bare red "Upload failed" with
					// nothing anywhere to say which outcome produced it. Name the outcome, and give the row
					// copy that describes what actually happens next — the session is still
					// resume-deliverable, so the paced drain keeps coming back for it.
					console.warn("[Upload] Resume returned an unexpected outcome; keeping the session for the retry drain", {
						sessionId: session.id,
						// Redacted: a file name is caller-controlled text and has carried signed upload URLs
						// before (adminUploadDrain.claim-failure-parity.contract.spec.ts pins that).
						fileName: redactUploadLogText(session.fileName),
						outcome: typeof result === "symbol" ? String(result) : result,
						outcomeType: typeof result
					});
					// `markResumedFileRetrying` (not `markResumedFileFailed`): the row is deliverable by
					// resume, so it must not render red or count in the dialog's "N uploads failed" header,
					// which means "these need you to do something". The drain's OWN bookkeeping stays in the
					// `failed` bucket so the announcement/dedup semantics R-0 settled are untouched — and
					// that bucket's sentence already reads "…will retry automatically in a couple minutes."
					dialog.markResumedFileRetrying(
						session.id,
						session.targetPending ? uploadStartRetryMessage("this upload") : unexpectedOutcomeMessage(session.fileName)
					);
					context.counts.failed++;
					context.failedIds.add(session.id);
				}
			} finally {
				endBackgroundUploadAttempt(session.id);
			}
		},
		async onSessionError(session, error, context) {
			// An in-flight resume can reject after disposal; ignore its stale error callback.
			if (disposed) return;
			if (error instanceof UploadCancelledError) return;
			if (isConfirmedMigrationIntegrityError(error)) {
				const currentSession = await getFileUploadSessionById(session.id);
				let outcome: "needs-attention" | "skipped-not-owned" | "executed";
				if (currentSession && currentSession.uploadOwnerId === undefined && currentSession.uploadOwnerClaimId === undefined) {
					// Round 4 compatibility: a driver that already demoted and cleared its claim remains an
					// ordinary ownerless reconciliation. Keep it on the general gate's existing ownerless
					// writer-epoch path; the exact-claim handoff is only for the still-stamped release race.
					const { adminSessionStore, adminUploadLease } = await import("./adminChunkedUploadDriver");
					outcome = await runGuardedEffect(
						createAdminOwnedOrUnownedGate(adminUploadLease, adminSessionStore),
						currentSession,
						async () =>
							(await updateFileUploadSessionStatus(currentSession.id, "needs-attention", undefined, {
								lastError: error.message
							}))
								? "needs-attention"
								: "skipped-not-owned"
					);
				} else {
					// The shared driver's claim() stamped this exact mutable session before the failure.
					// Preserve that released claim as the terminal handoff authority: the re-fetch above
					// selects the ownerless legacy path only and never supplies this branch's expected claim.
					const committed = await commitExactClaimTerminalWrite(session, async (expectedIdentity) =>
						(await updateFileUploadSessionStatus(session.id, "needs-attention", expectedIdentity, {
							lastError: error.message
						}))
							? "needs-attention"
							: "skipped-not-owned"
					);
					outcome = committed.kind === "applied" ? committed.value : "skipped-not-owned";
				}
				if (outcome !== "needs-attention") return;
				host.$refs.fileUploadDialog.markResumedFileNeedsAttention(
					session.id,
					stoppedUploadMessage(error)
				);
				context.counts.needsAttention++;
				return;
			}
			console.warn("[Upload] Resuming upload failed, will retry automatically", {
				sessionId: session.id,
				...uploadFailureLogDetails(error)
			});
			// This log line has always said "will retry automatically", and it is true: the driver
			// leaves the row resume-deliverable and the paced drain returns for it. Only the ROW said
			// "Upload failed", with no message at all. R-0's rule is that running out of an attempt
			// ladder is a STATUS, not a verdict — so the row now says what the log says. Counts stay in
			// the `failed` bucket for the same reason as the fall-through above.
			host.$refs.fileUploadDialog.markResumedFileRetrying(
				session.id,
				session.targetPending ? uploadStartRetryMessage("this upload") : resumeRetryMessage(session.fileName)
			);
			context.counts.failed++;
			context.failedIds.add(session.id);
		},
		async afterDrain(context, sessions) {
			// R-0: schedule the timed wake FIRST, before the quiet-pass early return below. A pass that
			// discovered nothing is exactly the pass where a paced row is sitting on a future `retryAt`
			// with nothing else scheduled to come back for it — the silent stall this directive exists
			// to remove. The wake policy's own recovery timer is a fixed 5/10/20s ladder, so it cannot
			// express "come back in fourteen minutes"; this one-shot can.
			await scheduleDueDrain(context);
			// LSP-19384: discover parked rows again, AFTER the attempts. The pass-start discovery cannot
			// see a row that parks DURING the pass, and such a row is exactly the one with no follow-up:
			// an evidence-terminal park is never requeued, and its `retryAt` is null, so nothing arms
			// another wake. Without this, a Try again that fails straight back to terminal leaves the row
			// on the generic needs-attention presentation with no Try again button, and the user cannot
			// act on it until something unrelated wakes the drain.
			//
			// Safe to run twice a pass because the tray's projection is idempotent by construction — it
			// derives every parked row from the snapshot rather than from what the previous call did.
			void surfaceTerminallyParkedUploads(host, () => !disposed);
			if (sessions.length === 0) {
				lastAnnouncedRecoveryBlockedSessionIds = null;
				return;
			}
			// Paced starts can be absent from the tray; exclude attempted rows whose snapshot predates completion.
			const attemptedSessionIds = new Set(sessions.map(session => session.id));
			const hasPendingStartRetries = sessions.some(
				session => session.targetPending && (context.retryingIds.has(session.id) || context.failedIds.has(session.id))
			) || context.pendingSessionSnapshot?.some(session =>
				session.targetPending && !attemptedSessionIds.has(session.id) &&
				host.$refs.fileUploadDialog?.ownsQueuedUploadSession?.(session.id, session.uploadId) !== true
			);
			// Selected batches announce failed starts once; retry passes only see partial subsets.
			for (const session of sessions) {
				if (!session.targetPending) continue;
				if (context.retryingIds.delete(session.id)) context.counts.pendingRetry--;
				if (context.failedIds.delete(session.id)) context.counts.failed--;
			}
			const { resumed, failed, lost, handedOff, needsAttention, recoveryBlocked, pendingRetry } = context.counts;
			const parts: string[] = [];
			const isPureSuccessPass =
				resumed > 0 &&
				context.verificationPendingIds.size === 0 &&
				(host.$refs?.fileUploadDialog?.finishingFiles?.length ?? 0) === 0 &&
				!hasPendingStartRetries &&
				(host.$refs?.fileUploadDialog?.retryingFiles?.length ?? 0) === 0 &&
				failed === 0 &&
				lost === 0 &&
				handedOff === 0 &&
				needsAttention === 0 &&
				recoveryBlocked === 0 &&
				pendingRetry === 0;
			// LSP-19543: "Files were uploaded successfully." speaks for every upload in this tab, not for
			// the drain pass alone. A user who adds files through the dialog while the drain is resuming
			// reloaded rows was otherwise told everything had finished the moment the drain settled.
			// Only the side that finishes last speaks; nothing is banked for later. The engine never
			// overlaps passes (`drainNow` returns early while `draining`, and `afterDrain` runs only
			// after every lane has settled), so the drain's own background count is always zero here and
			// the foreground count is the only thing left to check.
			const foregroundUploadBatchesInFlight = host.$refs?.fileUploadDialog?.activeUploadBatches ?? 0;
			if (isPureSuccessPass && foregroundUploadBatchesInFlight > 0) {
				console.info("[Upload] Drain pass finished while foreground uploads are still in flight; withholding the success toast", {
					activeUploadBatches: foregroundUploadBatchesInFlight,
					backgroundUploadAttemptsInFlight: backgroundUploadAttemptsInFlight(),
					resumed,
					sessionIds: sessions.map((session) => session.id)
				});
			} else if (isPureSuccessPass) parts.push("Files were uploaded successfully.");
			else {
				if (resumed) parts.push("Some files were uploaded successfully.");
				if (lost) parts.push(`${formatCount(lost)} ${lost === 1 ? "needs" : "need"} to be re-uploaded.`);
				if (handedOff) parts.push(`${formatCount(handedOff)} will finish in the background.`);
				if (needsAttention)
					parts.push(`${formatCount(needsAttention)} couldn't be completed automatically; local data was preserved.`);
				if (recoveryBlocked) parts.push(RECOVERY_BLOCKED_TOAST_MESSAGE(formatCount(recoveryBlocked), recoveryBlocked));
				if (pendingRetry) parts.push(`${formatCount(pendingRetry)} will keep retrying automatically.`);
				if (failed) parts.push(`${formatCount(failed)} couldn't be completed and will retry automatically in a couple minutes.`);
			}
			const sameSessionIdSet = (rememberedIds: Set<string> | null | undefined, currentIds: Set<string>) =>
				!!rememberedIds && rememberedIds.size === currentIds.size && [...currentIds].every((id) => rememberedIds.has(id));
			// Dedup compares exact session ID sets. Retry and failure buckets appearing, disappearing, or
			// changing IDs are new information. Recovery-blocked is a one-pass transition bucket: once the
			// durable row is parked it disappears from pending discovery without representing recovery, so
			// that expected disappearance must remain quiet.
			const retriesUnchanged =
				pendingRetry > 0
					? sameSessionIdSet(lastAnnouncedRetrySessionIds, context.retryingIds)
					: lastAnnouncedRetrySessionIds === null;
			const failuresUnchanged =
				failed > 0 ? sameSessionIdSet(lastAnnouncedFailedSessionIds, context.failedIds) : lastAnnouncedFailedSessionIds === null;
			const recoveryBlockedUnchanged =
				recoveryBlocked === 0 || sameSessionIdSet(lastAnnouncedRecoveryBlockedSessionIds, context.recoveryBlockedIds);
			// Sessions that resumed/were lost/handed off/need attention are always fresh, user-actionable
			// news (a state transition just happened), so their presence forces a toast regardless of
			// whether the retry or failed sentences repeat the previous pass. Recovery-blocked is a
			// durable parked state, so it joins the exact-ID dedup rather than pretending every observation
			// is a new transition. Only when the pass is
			// otherwise "quiet" do we compare the retry and failed sentences against what was last
			// announced — and BOTH must be unchanged for the whole toast to be suppressed, since either
			// sentence appearing, disappearing, or changing its session set is new information worth
			// surfacing.
			const hasOtherActivity = resumed > 0 || lost > 0 || handedOff > 0 || needsAttention > 0;
			const isRepeatOfLastAnnouncement = !hasOtherActivity && retriesUnchanged && failuresUnchanged && recoveryBlockedUnchanged;
			if (parts.length && !isRepeatOfLastAnnouncement) {
				// `needsAttention` is deliberately NOT in the error arm. Red is for outcomes the customer
				// has to act on because something they supplied was wrong; a parked row is a good file
				// whose server-side target went away, and its own sentence already says the local data was
				// preserved. `lost` stays red — there the retained bytes really are gone and the customer
				// does have to re-upload.
				const type =
					lost || (failed && !resumed && !handedOff && !pendingRetry)
						? "error"
						: failed || needsAttention || pendingRetry || recoveryBlocked
							? "warning"
							: "success";
				host.toast[type](parts.join(" "), { timeout: calculateTimeout(parts.join(" ")) });
			} else if (parts.length && isRepeatOfLastAnnouncement) {
				// Record the comparison state when suppressing a repeat so the decision is diagnosable.
				console.warn("[Upload] Suppressing repeat upload-drain announcement", {
					hasOtherActivity,
					retriesUnchanged,
					failuresUnchanged,
					recoveryBlockedUnchanged,
					pendingRetry,
					failed,
					currentRetryingIds: [...context.retryingIds],
					currentFailedIds: [...context.failedIds],
					currentRecoveryBlockedIds: [...context.recoveryBlockedIds],
					lastAnnouncedRetrySessionIds: lastAnnouncedRetrySessionIds ? [...lastAnnouncedRetrySessionIds] : null,
					lastAnnouncedFailedSessionIds: lastAnnouncedFailedSessionIds ? [...lastAnnouncedFailedSessionIds] : null,
					lastAnnouncedRecoveryBlockedSessionIds: lastAnnouncedRecoveryBlockedSessionIds
						? [...lastAnnouncedRecoveryBlockedSessionIds]
						: null
				});
			}
			// A quiet pass has no announcement. Preserve the prior snapshots so a later identical event
			// remains suppressed instead of being treated as a new announcement.
			if (parts.length) {
				// Clone snapshots so the private comparison state cannot alias the host mirror or pass
				// collections.
				lastAnnouncedRetrySessionIds = pendingRetry ? new Set(context.retryingIds) : null;
				lastAnnouncedFailedSessionIds = failed ? new Set(context.failedIds) : null;
				host._lastAnnouncedUploadRetrySessionIds = lastAnnouncedRetrySessionIds ? new Set(lastAnnouncedRetrySessionIds) : null;
				host._lastAnnouncedUploadFailedSessionIds = lastAnnouncedFailedSessionIds ? new Set(lastAnnouncedFailedSessionIds) : null;
			}
			// Unlike recurring retry/failure buckets, recovery-blocked records a transition observed only
			// while the row was pending. Clear its snapshot on the following pass even when that pass has no
			// toast parts, so a genuine later transition of the same session can be announced again.
			lastAnnouncedRecoveryBlockedSessionIds = recoveryBlocked ? new Set(context.recoveryBlockedIds) : null;
		},
		onDrainError(error) {
			console.error("[Upload] Error checking for pending file uploads", uploadFailureLogDetails(error));
		}
	});
	engineRef.current = engine;
	const engineDispose = engine.dispose.bind(engine);
	return Object.assign(engine, {
		// These public wake controls must also be terminal after disposal. Guard them before touching
		// the shared timer so a disposed drain cannot block a replacement's recovery schedule.
		scheduleRecovery(resetBackoff?: boolean) {
			if (disposed) return;
			wakePolicy.scheduleRecovery(resetBackoff);
		},
		clearRecovery() {
			if (disposed) return;
			wakePolicy.clearRecovery();
		},
		runStartupReclaim,
		dispose() {
			// Set the local guard before engine cleanup so in-flight callbacks observe terminal state.
			disposed = true;
			clearDueDrain();
			if (schedulerHandoverListener) {
				window.removeEventListener("pagehide", schedulerHandoverListener);
				schedulerHandoverListener = undefined;
			}
			engineDispose();
		}
	});
}
